import { useState } from 'react';
import { SEO } from '../components/SEO';
import CheckerForm from '../components/protocol-checker/CheckerForm';
import CheckerResults from '../components/protocol-checker/CheckerResults';
import LoadingState from '../components/protocol-checker/LoadingState';

export default function ProtocolCheckerPage() {
  const [isChecking, setIsChecking] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const handleCheck = async (url) => {
    setIsChecking(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/check-compatibility`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Check failed. Please try again.');
      }

      const data = await response.json();
      setResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsChecking(false);
    }
  };

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Protocol Compatibility Checker",
    "description": "Check if your e-commerce store is compatible with AI shopping agent protocols ACP and UCP",
    "url": "https://siddharthkaul.com/protocol-checker",
    "applicationCategory": "BusinessApplication",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  return (
    <main className="pt-20" data-testid="protocol-checker-page">
      <SEO
        title="Protocol Compatibility Checker — Is Your Store AI-Agent Ready?"
        description="Check ACP & UCP protocol compatibility for your e-commerce store in 30 seconds. See if ChatGPT, Copilot, and Google Gemini shopping agents can transact on your site."
        keywords="ACP compatibility, UCP compatibility, AI shopping agents, agentic commerce protocol, universal commerce protocol, e-commerce AI readiness"
        canonicalUrl="/protocol-checker"
        schema={pageSchema}
      />

      {/* Hero */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <p className="text-[#3498DB] font-semibold tracking-[0.2em] uppercase text-sm mb-4">
              Free Tool
            </p>
            <h1 className="font-['Montserrat'] font-extrabold text-4xl md:text-5xl text-[#2C3E50] mb-4">
              Protocol Compatibility Checker
            </h1>
            <p className="text-xl text-[#64748B] mb-2">
              Is your store ready for AI shopping agents?
            </p>
            <p className="text-[#94A3B8]">
              Check ACP &amp; UCP compatibility in 30 seconds. Free.
            </p>
          </div>

          {!results && !isChecking && (
            <CheckerForm onSubmit={handleCheck} error={error} />
          )}

          {isChecking && <LoadingState />}

          {results && (
            <CheckerResults
              results={results}
              onCheckAnother={() => setResults(null)}
            />
          )}
        </div>
      </section>

      {/* Explainer */}
      <section className="py-16 md:py-20 bg-[#F8F9FA]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-[#3498DB] font-semibold tracking-[0.2em] uppercase text-sm mb-4">
              What This Checks
            </p>
            <h2 className="font-['Montserrat'] font-bold text-3xl md:text-4xl text-[#2C3E50]">
              Two Protocols, One Score
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl border border-slate-200 p-8 card-hover">
              <div className="w-12 h-12 rounded-lg bg-[#3498DB]/10 flex items-center justify-center mb-4">
                <span className="font-['Montserrat'] font-bold text-[#3498DB] text-lg">ACP</span>
              </div>
              <h3 className="font-['Montserrat'] font-bold text-xl text-[#2C3E50] mb-3">
                OpenAI Agentic Commerce Protocol
              </h3>
              <p className="text-[#64748B] leading-relaxed">
                The protocol used by ChatGPT and Microsoft Copilot to discover, browse, and
                complete purchases on your store autonomously. Checks platform support,
                checkout endpoints, and payment handlers.
              </p>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-8 card-hover">
              <div className="w-12 h-12 rounded-lg bg-[#3498DB]/10 flex items-center justify-center mb-4">
                <span className="font-['Montserrat'] font-bold text-[#3498DB] text-lg">UCP</span>
              </div>
              <h3 className="font-['Montserrat'] font-bold text-xl text-[#2C3E50] mb-3">
                Google Universal Commerce Protocol
              </h3>
              <p className="text-[#64748B] leading-relaxed">
                The protocol used by Google Gemini for product discovery and purchase. Checks
                discovery endpoints, JSON-LD structured data, and Product schema.org markup.
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
